// One Workflow Definition row on the Work landing (brief §5.1): name + axis + description, the open-item
// and needs-a-decision counts, and the sweep cell — health, paused and pre-first-sweep states rendered
// honestly from the projection (G-182). The WHOLE row is the affordance that opens the board route via
// the onOpenBoard callback (navigation is callbacks-only — surface-contracts); it carries NO verdict
// control — verdicts live inside the opened card (brief §5.2). Machine state rides data attributes
// (data-definition / -state / -view / -decisions / -sweep-health) so tests assert behaviour (G-180).

import { Icon } from '../../../../app/Icon.tsx';
import type { WorkLandingDefinitionRow } from '../../projections.ts';
import { deriveSweepCell, type SweepHealth } from '../landing-model.ts';

/** The shared row grid — DefinitionList's column-head row mirrors it so the columns align. */
export const DEF_GRID =
  'grid grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_1.5rem] items-center gap-4 lg:grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_10.5rem_1.5rem]';

const SWEEP_TONE: Readonly<Record<SweepHealth, string>> = {
  ok: 'text-forest',
  attention: 'text-bracken',
  paused: 'text-text-muted',
  pending: 'text-text-subtle',
};

export interface DefinitionRowProps {
  readonly row: WorkLandingDefinitionRow;
  readonly onOpenBoard: (definitionId: string) => void;
}

/** A single definition row (the prototype's wk-def row, tokens only — G-181). */
export function DefinitionRow({ row, onOpenBoard }: DefinitionRowProps): React.JSX.Element {
  const sweep = deriveSweepCell(row);
  return (
    <button
      type="button"
      onClick={() => onOpenBoard(row.id)}
      // The label OVERRIDES the row's rendered text for AT, so the paused state must ride it —
      // a reader traversing the list hears which workflow is paused, not just sighted colour.
      aria-label={
        row.state === 'paused' ? `Open board: ${row.name} — paused` : `Open board: ${row.name}`
      }
      data-definition={row.id}
      data-state={row.state}
      data-view={row.view}
      data-decisions={row.needsDecision}
      data-sweep-health={sweep.health}
      className={`${DEF_GRID} w-full px-5 py-4 text-left hover:bg-surface-sunken`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-text">{row.name}</span>
        <span className="mt-0.5 block truncate text-xs text-text-muted">
          <span className="ritona-numeric uppercase tracking-wide text-forest">{row.view}</span>
          {' · '}
          {row.description}
        </span>
      </span>
      <span className="ritona-numeric text-xs text-text-muted">
        <span className="ritona-voice text-xl tracking-snug text-text">{row.openItems}</span> open
      </span>
      {row.needsDecision > 0 ? (
        <span className="ritona-numeric text-xs text-bracken">
          <span className="ritona-voice text-xl tracking-snug">{row.needsDecision}</span> waiting
        </span>
      ) : (
        <span className="text-xs text-text-subtle">—</span>
      )}
      <span className={`hidden text-xs lg:block ${SWEEP_TONE[sweep.health]}`}>
        <span className="ritona-numeric flex items-center gap-1">
          {sweep.health === 'ok' ? <Icon name="check" size={12} /> : null}
          {sweep.label}
        </span>
        <span className="mt-0.5 block text-text-subtle">{sweep.sub}</span>
      </span>
      <span className="flex justify-end text-text-subtle">
        <Icon name="chevron" size={16} />
      </span>
    </button>
  );
}
